import apiClient from '@/shared/lib/api'
import type { AccountSummary, CampaignDetail, DateRange } from '../types'

const BUDGET_ALLOCATION_BASE = '/budget-allocation'

// Response wrapper type
interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
}

// Connection status response from API
interface ConnectionStatusResponse {
  connected: boolean
  account_id?: string
  account_name?: string
  currency?: string
  last_sync_at?: string
  connected_at?: string
  status?: string
  message?: string
}

// Format date to YYYY-MM-DD
function formatDate(date: Date): string {
  return date.toISOString().split('T')[0]
}

// Get default date range (last 7 days)
export function getDefaultDateRange(): DateRange {
  const endDate = new Date()
  const startDate = new Date()
  startDate.setDate(startDate.getDate() - 7)

  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
  }
}

// Budget Allocation API Service - matches Analytics service pattern
export const budgetAllocationApi = {
  // Get Meta connection status
  async getConnectionStatus(): Promise<ApiResponse<ConnectionStatusResponse>> {
    try {
      const response = await apiClient.get<ConnectionStatusResponse>(
        `${BUDGET_ALLOCATION_BASE}/connection`
      )
      return { success: true, data: response.data }
    } catch (error: unknown) {
      console.error('Error fetching connection status:', error)
      const err = error as { message?: string }
      return { success: false, error: err.message || 'Failed to fetch connection status' }
    }
  },

  // Get account summary
  async getSummary(startDate: string, endDate: string): Promise<ApiResponse<AccountSummary>> {
    try {
      const response = await apiClient.get<{ summary: AccountSummary }>(
        `${BUDGET_ALLOCATION_BASE}/summary`,
        {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
        }
      )
      return { success: true, data: response.data.summary }
    } catch (error: unknown) {
      console.error('Error fetching summary:', error)
      const err = error as { message?: string }
      return { success: false, error: err.message || 'Failed to fetch summary' }
    }
  },

  // Get campaigns with metrics
  async getCampaigns(startDate: string, endDate: string): Promise<ApiResponse<CampaignDetail[]>> {
    try {
      const response = await apiClient.get<{ campaigns: CampaignDetail[] }>(
        `${BUDGET_ALLOCATION_BASE}/campaigns`,
        {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
        }
      )
      return { success: true, data: response.data.campaigns || [] }
    } catch (error: unknown) {
      console.error('Error fetching campaigns:', error)
      const err = error as { message?: string }
      return { success: false, error: err.message || 'Failed to fetch campaigns' }
    }
  },

  // Connect Meta ad account
  async connectMeta(request: {
    access_token: string
    ad_account_id: string
  }): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      // Backend expects account_id, not ad_account_id
      const payload = {
        access_token: request.access_token,
        account_id: request.ad_account_id,
      }
      const response = await apiClient.post<{ success: boolean; message: string }>(
        `${BUDGET_ALLOCATION_BASE}/connect`,
        payload
      )
      return { success: true, data: response.data }
    } catch (error: unknown) {
      console.error('Error connecting Meta:', error)
      const err = error as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, error: err.response?.data?.message || err.message || 'Failed to connect' }
    }
  },

  // Disconnect Meta ad account
  async disconnectMeta(): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      const response = await apiClient.delete<{ success: boolean; message: string }>(
        `${BUDGET_ALLOCATION_BASE}/connection`
      )
      return { success: true, data: response.data }
    } catch (error: unknown) {
      console.error('Error disconnecting:', error)
      const err = error as { message?: string }
      return { success: false, error: err.message || 'Failed to disconnect' }
    }
  },

  // Trigger manual sync
  async syncData(): Promise<ApiResponse<{ success: boolean; message: string; synced_at: string }>> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string; synced_at: string }>(
        `${BUDGET_ALLOCATION_BASE}/sync`
      )
      return { success: true, data: response.data }
    } catch (error: unknown) {
      console.error('Error syncing data:', error)
      const err = error as { message?: string }
      return { success: false, error: err.message || 'Failed to sync data' }
    }
  },

  // Get available ad accounts
  async getAdAccounts(accessToken: string): Promise<
    ApiResponse<
      Array<{
        id: string
        account_id: string
        name: string
        currency: string
      }>
    >
  > {
    try {
      const response = await apiClient.get<{
        ad_accounts: Array<{ id: string; account_id: string; name: string; currency: string }>
      }>(`${BUDGET_ALLOCATION_BASE}/meta/ad-accounts`, {
        params: {
          access_token: accessToken,
        },
      })
      return { success: true, data: response.data.ad_accounts }
    } catch (error: unknown) {
      console.error('Error fetching ad accounts:', error)
      const err = error as { message?: string }
      return { success: false, error: err.message || 'Failed to fetch ad accounts' }
    }
  },
}

export default budgetAllocationApi
